import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getData, getcountries} from '../../../services/management/refundStatus/refundServices';
import { Modal, Button, Form, Table } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import Header from '../../../components/Layout/Header';
import Footer from '../../../components/Layout/Footer';
import Sidebar from '../../../components/Layout/Sidebar';
import Spinner from '../../../components/Spinner';

// ── Parse remarks stored as JSON string or array ──────────────────────────────
const parseRemarksHistory = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
  } catch (_) {}
  return [];
};

// ── Format ISO → "22 Jun 2026, 10:30 AM" ─────────────────────────────────────
const formatTimestamp = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

const RefundApproved = () => {
  const [dataList, setDataList]       = useState([]);
  const [form, setForm]               = useState({ id: '', refund_date: '', transaction_id: '' });
  const [showViewModal, setShowViewModal] = useState(false);
  const [searchText, setSearchText]   = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading]         = useState(true);
  // Filter States
  const [startDate, setStartDate]           = useState('');
  const [endDate, setEndDate]               = useState('');
  const [seriesFilter, setSeriesFilter]     = useState('');
  const [subDomainFilter, setSubDomainFilter] = useState('');
  const [countryFilter, setCountryFilter]   = useState('');
  const [dropdownData, setDropdownData]     = useState({ countries: [] });

  // ── Remarks history (read-only) ───────────────────────────────────────────
  const [remarksHistory, setRemarksHistory] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const data = await getData({ status: 1 });
      setDataList(data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  }, []);

  const fetchDropdownData = useCallback(async () => {
    try {
      const [country] = await Promise.all([getcountries()]);
      setDropdownData({ countries: country });
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
    }
  }, []);

  useEffect(() => {
    const fetchDataAsync = async () => {
      setLoading(true);
      await fetchData();
      await fetchDropdownData();
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setLoading(false);
    };
    fetchDataAsync();
  }, [fetchData, fetchDropdownData]);

  const openViewModal = (data) => {
    setForm(data);
    setRemarksHistory(parseRemarksHistory(data.remarks));
    setShowViewModal(true);
  };

  const handleSearchChange = (e) => setSearchText(e.target.value);
  const handlePageChange   = (page) => setCurrentPage(page);

  const getCurrencySymbol = (currencyCode) => {
    switch (currencyCode?.toUpperCase()) {
      case 'USD': return '$'; case 'EUR': return '€'; case 'INR': return '₹';
      case 'GBP': return '£'; case 'RUB': return '₽'; case 'THB': return '฿';
      default: return currencyCode || '';
    }
  };

  const getCountryName = (code) => {
    const country = dropdownData.countries.find(c => c.code === code);
    return country ? country.name : code;
  };

  const filteredData = useMemo(() => {
    const startDateObj = startDate ? new Date(startDate) : null;
    const endDateObj   = endDate   ? new Date(endDate)   : null;
    return dataList
      .filter((data) => {
        const matchesText =
          data.name.toLowerCase().includes(searchText.toLowerCase()) ||
          data.sub_domain.toLowerCase().includes(searchText.toLowerCase()) ||
          data.actual_amount.toString().includes(searchText.toLowerCase()) ||
          data.refund_amount.toString().includes(searchText.toLowerCase());
        const date    = data.updatedAt ? new Date(data.updatedAt) : null;
        const inRange = startDateObj && endDateObj ? date >= startDateObj && date <= endDateObj : true;
        const matchSeries    = !seriesFilter    || data.series     === seriesFilter;
        const matchSubDomain = !subDomainFilter || data.sub_domain === subDomainFilter;
        const matchCountry   = !countryFilter   || data.countries  === countryFilter;
        return matchesText && inRange && matchSeries && matchSubDomain && matchCountry;
      })
      .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
  }, [dataList, searchText, seriesFilter, subDomainFilter, countryFilter, startDate, endDate]);

  const columns = [
    { name: '#', selector: (row, index) => (currentPage - 1) * 10 + (index + 1), width: '80px' },
    { name: 'Series',     selector: (row) => row.series,              sortable: true },
    { name: 'Sub Domain', selector: (row) => row.sub_domain || 'N/A', sortable: true },
    { name: 'Name',       selector: (row) => row.name || 'N/A',       sortable: true },
    { name: 'Country',    selector: (row) => getCountryName(row.countries || 'N/A'), sortable: true },
    {
      name: 'Actual Amount',
      selector: (row) => <b className="fs-4">{getCurrencySymbol(row.currency)} {row.actual_amount || 'N/A'}</b>,
      sortable: true,
    },
    {
      name: 'Refund Amount',
      selector: (row) => <b className="fs-4">{getCurrencySymbol(row.currency)} {row.refund_amount || 'N/A'}</b>,
      sortable: true,
    },
    {
      name: 'Actions',
      cell: (row) => (
        <Button variant="primary" size="sm" onClick={() => openViewModal(row)} className="me-2">
          <i className="align-middle far fa-fw fa-eye"></i>
        </Button>
      ),
    },
  ];

  return (
    <div className="d-flex">
      <Sidebar />
      <div className="main-content w-100">
        <Header />
        <div className="row m-3 d-flex flex-column gap-3">
          <div className="d-flex gap-2">
            <div className="col-md-2">
              <span>Series</span>
              <select className="form-control" value={seriesFilter} onChange={(e) => setSeriesFilter(e.target.value)}>
                <option value="">All Series</option>
                {Array.from(new Set(dataList.map(item => item.series))).map((series, idx) => (
                  <option key={idx} value={series}>{series}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <span>Sub Domain</span>
              <select className="form-control" value={subDomainFilter} onChange={(e) => setSubDomainFilter(e.target.value)}>
                <option value="">All Subdomains</option>
                {Array.from(new Set(dataList.map(item => item.sub_domain))).map((sd, idx) => (
                  <option key={idx} value={sd}>{sd}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <span>Countries</span>
              <select className="form-control" value={countryFilter} onChange={(e) => setCountryFilter(e.target.value)}>
                <option value="">All Countries</option>
                {dropdownData.countries.map((country) => (
                  <option key={country.code} value={country.code}>{country.name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <span>Start Date</span>
              <input type="date" className="form-control" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="col-md-2">
              <span>End Date</span>
              <input type="date" className="form-control" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '300px' }}>
            <Spinner />
          </div>
        ) : (
          <div>
            <div className="container-fluid p-4">
              <div className="row">
                <div className="col-8">
                  <p className="fs-4">Revenue Management / Refund status / Approved Refund Details</p>
                </div>
              </div>

              <DataTable
                columns={columns} data={filteredData} pagination highlightOnHover responsive striped
                paginationPerPage={10} paginationRowsPerPageOptions={[10, 20, 50, 100, 200, 500]}
                onChangePage={handlePageChange} subHeader
                subHeaderComponent={
                  <input className="form-control" type="text" placeholder="Search"
                    value={searchText} onChange={handleSearchChange}
                  />
                }
              />

              {/* ── View Modal ── */}
              <Modal className="modal-lg" backdrop="static" show={showViewModal} onHide={() => setShowViewModal(false)}>
                <Modal.Header closeButton>
                  <Modal.Title>View</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <Table striped bordered hover responsive className="table-sm">
                    <thead>
                      <tr><th className="bg-secondary text-white" colSpan={2}>Approved Refund Details</th></tr>
                    </thead>
                    <tbody>
                      <tr><td>Name</td>             <td>{form.name}</td></tr>
                      <tr><td>Payment ID</td>       <td>{form.pr_id}</td></tr>
                      <tr><td>Email</td>            <td>{form.email}</td></tr>
                      <tr><td>Phone No</td>         <td>{form.phone_no}</td></tr>
                      <tr><td>Country</td>          <td>{getCountryName(form.countries) || 'Unknown Country'}</td></tr>
                      <tr><td>Currency</td>         <td>{form.currency}</td></tr>
                      <tr><td>Actual Amount</td>    <td>{form.actual_amount}</td></tr>
                      <tr><td>Refund Amount</td>    <td>{form.refund_amount}</td></tr>
                      <tr><td>Remaining Amount</td> <td>{form.remaining_amount}</td></tr>
                      <tr><td>Refunded Date</td>    <td>{form.refund_date}</td></tr>
                      <tr><td>Transaction Id</td>   <td>{form.transaction_id}</td></tr>
                      <tr><td>Series</td>           <td>{form.series}</td></tr>
                      <tr><td>Subdomain</td>        <td>{form.sub_domain}</td></tr>
                      <tr><td>Refund Through</td>   <td>{form.refund_through}</td></tr>
                    </tbody>

                    {form.paypal_email && (
                      <>
                        <thead><tr><th className="bg-secondary text-white" colSpan={2}>PayPal Details</th></tr></thead>
                        <tbody><tr><td>PayPal Email/ID</td><td>{form.paypal_email}</td></tr></tbody>
                      </>
                    )}

                    {(form.bank_name || form.bank_address || form.bank_country || form.postal_code ||
                      form.account_number || form.swift_code_ifsc_code || form.iban_code) && (
                      <>
                        <thead><tr><th className="bg-secondary text-white" colSpan={2}>Bank Details</th></tr></thead>
                        <tbody>
                          {form.bank_name            && <tr><td>Bank Name</td>       <td>{form.bank_name}</td></tr>}
                          {form.bank_address         && <tr><td>Bank Address</td>    <td>{form.bank_address}</td></tr>}
                          {form.bank_country         && <tr><td>Bank Country</td>    <td>{getCountryName(form.bank_country) || 'Unknown Country'}</td></tr>}
                          {form.postal_code          && <tr><td>Postal Code</td>     <td>{form.postal_code}</td></tr>}
                          {form.account_number       && <tr><td>Account Number</td>  <td>{form.account_number}</td></tr>}
                          {form.swift_code_ifsc_code && <tr><td>Swift/IFSC Code</td> <td>{form.swift_code_ifsc_code}</td></tr>}
                          {form.iban_code            && <tr><td>IBAN Code</td>       <td>{form.iban_code}</td></tr>}
                        </tbody>
                      </>
                    )}
                  </Table>

                  {/* ── Remarks history — read-only ── */}
                  {remarksHistory.length > 0 && (
                    <div className="col-md-12 mb-3">
                      <Form.Label className="fs-5 text-dark fw-semibold">
                        Remarks History
                        <span className="badge bg-secondary ms-2" style={{ fontSize: '0.72rem' }}>
                          {remarksHistory.length}
                        </span>
                      </Form.Label>
                      <div className="border rounded" style={{ maxHeight: '200px', overflowY: 'auto', backgroundColor: '#f8f9fa' }}>
                        {[...remarksHistory].reverse().map((entry, idx) => (
                          <div key={idx} className="px-3 py-2 border-bottom" style={{ borderLeft: '3px solid #0d6efd' }}>
                            <div className="d-flex justify-content-between align-items-baseline">
                              <small className="text-muted d-block mb-1">🕒 {formatTimestamp(entry.timestamp)}</small>
                              {entry.user && (
                                <span className="badge bg-info text-dark" style={{ fontSize: '0.72rem' }}>{entry.user}</span>
                              )}
                            </div>
                            <p className="mb-0 text-dark" style={{ fontSize: '0.9rem' }}>{entry.remark}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </Modal.Body>
              </Modal>
            </div>
            <Footer />
          </div>
        )}
      </div>
    </div>
  );
};

export default RefundApproved;
